import { match, NO_ELEMENT } from 'utils';
import styles from 'views/ProductPage/configurators/variant/themes/BGH/styles.scss';
import cn from 'classnames';
import { AttributeType } from 'models/AttributeType';
import { ToolTip } from 'components/Shared/ToolTip';
import { ConfiguratorProps, ModalProps } from 'views/ProductPage/configurators';
import { Attribute, useConfigurator } from 'views/ProductPage/configurators/useConfigurator';
import { rules } from 'views/ProductPage/configurators/rules';
import { SlidingDoorConfigurator } from 'models/SlidingDoorConfigurator';
import { Doors } from './doors';
import { DoorWidth } from './doorWidth';
import { Height } from './height';
import { AttributeFilter } from 'views/ProductPage/configurators/variant/attributeFilter';
import { AttributeImage } from 'views/ProductPage/configurators/variant/attributeImage';
import { ReactElement } from 'react';
import { AttributeSelect } from 'views/ProductPage/configurators/variant/attributeSelect';
import { AttributeNumeric } from 'views/ProductPage/configurators/variant/attributeNumeric';
import { masterAttribute } from '../variant/themes/BGH';

interface OwnProps extends ConfiguratorProps, ModalProps {
  configurator: SlidingDoorConfigurator;
}

const doorWidth = (overlap: number) => (width: number, doors: number) =>
  Math.ceil((width + overlap * doors) / doors);
const widthVariant = (width: Pick<Attribute, 'values'>) => (doorSize: number) =>
  width.values
    .map(({ value }) => parseFloat(value))
    .filter(v => !Number.isNaN(v))
    .sort((a, b) => a - b)
    .find(value => doorSize <= value);

export default function SlidingDoor(props: OwnProps) {
  const { attributes, configurator } = useConfigurator<SlidingDoorConfigurator>();
  const validate = rules(props);
  const { isMaster, isMasterSelected, hasMasterConstraint } = masterAttribute({
    configurator,
    configAttributes: attributes
  });
  const findWidthVariant = widthVariant(
    configurator.attributes.find(a => a.id === 'widthMm') || { values: [] }
  );
  const calcDoorWidth = doorWidth(configurator.overlap);

  const component = match<Attribute, ReactElement>()
    .when(
      a => a.id === 'widthMm',
      a => (
        <DoorWidth
          {...{ ...a, validate, findWidthVariant, autoAdjustValue: true, calcDoorWidth }}
        />
      )
    )
    .when(
      a => a.id === 'doors',
      a => <Doors {...{ ...a, validate, findWidthVariant, calcDoorWidth, autoAdjustValue: true }} />
    )
    .when(
      a => a.id === 'heightMm',
      a => <Height {...{ ...a, validate }} />
    )
    .when(
      a => a.type === AttributeType.Select,
      a => <AttributeSelect {...{ ...a }} />
    )
    .when(
      a => a.type === AttributeType.AttributeFilter,
      a => <AttributeFilter {...{ ...a }} />
    )
    .when(
      a => a.type === AttributeType.NumericInput,
      a => <AttributeNumeric {...{ ...a, validate }} />
    )
    .when(
      a => a.type === AttributeType.Image,
      v => <AttributeImage {...{ ...v, isMaster, hasMasterConstraint }} />
    )
    .or(<></>)
    .toFunc();

  const Control = (a: Attribute) => {
    const shouldShow = isMaster(a.id) || isMasterSelected();
    if (hasMasterConstraint && !shouldShow) {
      return NO_ELEMENT;
    }
    const control = component(a);

    return (
      <div
        key={a.name}
        className={cn(styles.cell, {
          [styles.two]: a.type !== AttributeType.Image,
          [styles.one]: a.type === AttributeType.Image
        })}
      >
        <label>
          {/* TODO: Clean up when redesign is done */}
          <span className={styles.hideLabelForRD3}>{a.name}</span>
          {a.toolTip && <ToolTip text={a.toolTip} />}
        </label>
        {control}
      </div>
    );
  };

  return (
    <div className={cn(styles.base, { [styles.mobile]: props.isMobile })}>
      {attributes.map(Control)}
    </div>
  );
}
